import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Home, CheckCircle2, X, Loader2 } from "lucide-react";

const STATUS_LABELS = {
    active: { label: "Ancora disponibile", color: "text-gold border-gold/40 bg-gold/5", icon: "↻" },
    sold: { label: "Venduto", color: "text-green-300 border-green-700/40 bg-green-900/20", icon: "✓" },
    rented: { label: "Affittato", color: "text-green-300 border-green-700/40 bg-green-900/20", icon: "✓" },
    removed: { label: "Rimosso", color: "text-red-300 border-red-700/40 bg-red-900/20", icon: "✗" },
};

export default function ListingStatusUpdate() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const initialAction = params.get("action");
    const [prop, setProp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [done, setDone] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) { setError("Token mancante"); setLoading(false); return; }
        api.get(`/listing-status/by-token/${token}`)
            .then((r) => setProp(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Token non valido"))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (!loading && prop && initialAction && !done && !busy) {
            update(initialAction === "venduto" ? "sold" : initialAction === "affittato" ? "rented" : initialAction);
        }
        // eslint-disable-next-line
    }, [loading, prop, initialAction]);

    const update = async (status) => {
        setBusy(true); setError(null);
        try {
            await api.post(`/listing-status/by-token/${token}`, { status });
            setDone(status);
        } catch (e) { setError(e?.response?.data?.detail || "Errore aggiornamento"); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="pt-32 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Caricamento...</div>;
    if (error && !prop) return <div className="pt-32 max-w-xl mx-auto px-6 text-center"><Home size={32} className="text-gold mx-auto mb-4" /><p className="text-ink-400">{error}</p></div>;

    return (
        <div data-testid="status-update-page" className="pt-32 pb-24 max-w-2xl mx-auto px-6">
            <div className="border border-white/10 p-10 bg-ink-900/40">
                <span className="label-eyebrow">Aggiornamento annuncio</span>
                <h1 className="font-serif text-3xl text-ink-50 mt-3">Com'è andata?</h1>

                <div className="mt-6 flex gap-4 items-center border border-white/10 p-4 bg-ink-950">
                    {prop?.photo && <img src={prop.photo} alt="" className="w-20 h-20 object-cover" />}
                    <div className="flex-1">
                        <div className="font-serif text-lg text-ink-50">{prop?.title}</div>
                        <div className="text-xs text-ink-400">{prop?.city} · {formatPrice(prop?.price)}{prop?.transaction_type === "affitto" ? "/mese" : ""}</div>
                    </div>
                </div>

                {done ? (
                    <div data-testid="status-done" className={`mt-8 p-5 border ${STATUS_LABELS[done]?.color || "text-ink-100"} flex items-start gap-3 text-sm`}>
                        {done === "removed" ? <X size={18} /> : <CheckCircle2 size={18} />}
                        <div>
                            <div className="font-medium">Stato aggiornato: {STATUS_LABELS[done]?.label}</div>
                            <p className="mt-1 text-xs">Grazie! L'annuncio è stato aggiornato. Gli utenti vedranno subito la disponibilità corretta.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-ink-400 mt-6">Aiutaci a tenere vendidirect.it aggiornato: come procede questo annuncio?</p>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                data-testid="status-sold"
                                onClick={() => update(prop?.transaction_type === "affitto" ? "rented" : "sold")}
                                disabled={busy}
                                className="py-4 bg-gold text-black text-sm font-medium hover:bg-gold-soft flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 size={16} /> Già {prop?.transaction_type === "affitto" ? "affittato" : "venduto"}
                            </button>
                            <button
                                data-testid="status-active"
                                onClick={() => update("active")}
                                disabled={busy}
                                className="py-4 border border-gold/40 text-gold text-sm font-medium hover:bg-gold/10 flex items-center justify-center gap-2"
                            >
                                ↻ Ancora disponibile
                            </button>
                            <button
                                data-testid="status-removed"
                                onClick={() => { if (window.confirm("Rimuovere l'annuncio dal portale?")) update("removed"); }}
                                disabled={busy}
                                className="sm:col-span-2 py-3 border border-white/15 text-ink-400 text-xs hover:border-red-400 hover:text-red-300 flex items-center justify-center gap-2"
                            >
                                <X size={14} /> Rimuovi annuncio
                            </button>
                        </div>
                        {error && <div className="mt-4 text-xs text-red-300">{error}</div>}
                    </>
                )}
                <Link to="/" className="inline-block mt-8 text-xs text-gold hover:underline">← Vai al sito</Link>
            </div>
        </div>
    );
}
